/**
 * pan close <id> — Human-gated close-out ceremony for completed issues.
 *
 * Verifies merge, archives artifacts, cleans up workspace/agent state,
 * closes the issue on the tracker, and applies the `closed-out` label.
 */

import chalk from 'chalk';
import type { Command } from 'commander';
import { Effect } from 'effect';
import { existsSync, readFileSync } from 'fs';
import { join } from 'path';
import { homedir } from 'os';
import { userInfo } from 'os';
import { execFile } from 'child_process';
import { promisify } from 'util';
import { closeOut, type WorkflowResult } from '../../lib/lifecycle/index.js';
import { resolveProjectFromIssueSync, extractTeamPrefix, findProjectByTeamSync } from '../../lib/projects.js';
import { resolveBareNumericIdSync } from '../../lib/issue-id.js';
import { mapGitHubStateToCanonical, type CanonicalState } from '../../core/state-mapping.js';
import { acceptFlagFor, DOD_ROWS, type DodRowId } from '../../lib/lifecycle/dod.js';

const execFileAsync = promisify(execFile);

interface CloseOutOptions {
  force?: boolean;
  json?: boolean;
  [key: string]: boolean | undefined;
}

function optionNameForRow(id: DodRowId): string {
  return `accept${id.split('-').map(part => part[0]!.toUpperCase() + part.slice(1)).join('')}`;
}

function renderDodGate(result: WorkflowResult): void {
  if (!result.dodGate) return;
  console.log('Definition-of-Done gate: each row below is a step an issue must complete before close-out; MISS means the step has not verifiably happened.');
  console.log('ROW  ID            EXPECTED                                      OBSERVED                                      RESULT');
  for (const row of result.dodGate.rows) {
    const outcome = row.acceptedBy ? `MISS-ACCEPTED (${row.acceptedBy.by})` : row.status.toUpperCase();
    console.log(
      `${String(row.num).padEnd(4)} ${row.id.padEnd(13)} ${row.expected.padEnd(45)} ${row.observed.padEnd(45)} ${outcome}`,
    );
  }
  for (const row of result.dodGate.rows.filter(candidate => candidate.status === 'miss' && !candidate.acceptedBy)) {
    console.log(chalk.yellow(`Row ${row.num} (${row.id}) blocks close-out; ${`--accept-${row.id}`} records an explicit override.`));
  }
  console.log();
}

export function registerCloseCommand(program: Command): void {
  const command = program
    .command('close <id>')
    .description('Verify, clean up, and close issue on tracker')
    .option('--force', 'Skip confirmation prompt')
    .option('--json', 'Output as JSON');
  for (const row of DOD_ROWS.filter(candidate => candidate.overridable)) {
    command.option(
      acceptFlagFor(row),
      `Accept a missed DoD row ${row.num} (${row.title}) — recorded as an explicit override in the close-out record`,
    );
  }
  command.action((id, options) => closeOutCommand(id, options));
}

function getGitHubConfig(): { owner: string; repo: string; prefix: string } | null {
  const envFile = join(homedir(), '.overdeck.env');
  if (!existsSync(envFile)) return null;

  const content = readFileSync(envFile, 'utf-8');
  const reposMatch = content.match(/GITHUB_REPOS=(.+)/);
  if (!reposMatch) return null;

  const repoStr = reposMatch[1].trim();
  // Format: owner/repo:PREFIX or owner/repo
  const parts = repoStr.split(',')[0]; // Take first repo
  if (!parts) return null;

  const [ownerRepo, prefix] = parts.split(':');
  const [owner, repo] = ownerRepo.split('/');
  if (!owner || !repo) return null;

  return { owner, repo, prefix: prefix || repo.toUpperCase().replace(/-CLI$/, '').replace(/-/g, '') };
}

async function readGitHubCanonicalState(owner: string, repo: string, number: number): Promise<CanonicalState> {
  const { stdout } = await execFileAsync('gh', [
    'issue',
    'view',
    String(number),
    '--repo',
    `${owner}/${repo}`,
    '--json',
    'state,labels',
  ], { encoding: 'utf-8', timeout: 10000 });
  const parsed = JSON.parse(stdout) as { state?: string; labels?: Array<string | { name?: string }> };
  const labels = (parsed.labels ?? [])
    .map(label => typeof label === 'string' ? label : label.name)
    .filter((label): label is string => typeof label === 'string');
  return mapGitHubStateToCanonical(parsed.state ?? 'open', labels);
}

export interface GitHubCloseState {
  state: 'open' | 'closed';
  reason: 'completed' | 'not_planned' | null;
}

/**
 * Read the raw close state of a GitHub issue, including its close reason.
 *
 * This is the same `gh issue view` door used by closeOutCommand; it is exported
 * so other CLI commands (e.g. `pan task sweep`) can look up tracker state
 * without reaching past the tracker door.
 */
export async function readGitHubCloseState(owner: string, repo: string, number: number): Promise<GitHubCloseState> {
  const { stdout } = await execFileAsync('gh', [
    'issue',
    'view',
    String(number),
    '--repo',
    `${owner}/${repo}`,
    '--json',
    'state,stateReason',
  ], { encoding: 'utf-8', timeout: 10000 });
  const parsed = JSON.parse(stdout) as { state?: string; stateReason?: string | null };
  const stateLower = (parsed.state ?? 'open').toLowerCase();
  const reasonLower = parsed.stateReason?.toLowerCase();
  return {
    state: stateLower === 'closed' ? 'closed' : 'open',
    reason: reasonLower === 'not_planned' ? 'not_planned' : reasonLower === 'completed' ? 'completed' : null,
  };
}

export async function closeOutCommand(id: string, options: CloseOutOptions): Promise<void> {
  const issueId = resolveBareNumericIdSync(id);
  if (!issueId) {
    console.error(chalk.red(`Could not resolve issue ID "${id}"`));
    console.error(chalk.dim(
      'Pass a fully-qualified ID like "PAN-1148", or ensure the agent state dir exists at ~/.overdeck/agents/agent-<prefix>-<num>/',
    ));
    process.exit(1);
  }

  // Close-out caller guard. Close-out is destructive (closes the tracker issue +
  // tears down the workspace/branches), so it is gated to:
  //   - `conv-*` — operator-driven conversations (the human is in the loop and
  //     explicitly asked). PAN-1621.
  //   - `flywheel-*` — the autonomous lifecycle orchestrator, now trusted to run
  //     close-out as the final step of the pipeline it manages. The close-out's
  //     own verify-merged step + the verifying-on-main state are the safety net
  //     against closing unfinished work.
  // All other autonomous pipeline agents (`agent-*`, `planning-*`, `strike-*`,
  // `inspect-*`, …) remain barred.
  const agentId = process.env.OVERDECK_AGENT_ID;
  const isOperatorConversation = agentId?.startsWith('conv-') ?? false;
  const isFlywheelOrchestrator = agentId?.startsWith('flywheel-') ?? false;
  if (agentId && !isOperatorConversation && !isFlywheelOrchestrator) {
    console.error(chalk.red('Close-out is not permitted for this agent. Only operator conversations (conv-*) and the flywheel orchestrator may close out issues.'));
    process.exit(1);
  }

  const issueLower = issueId.toLowerCase();
  const issueUpper = issueId.toUpperCase();

  // Resolve project
  const teamPrefix = extractTeamPrefix(issueId);
  const projectConfig = teamPrefix ? findProjectByTeamSync(teamPrefix) : null;
  let projectPath = '';

  if (projectConfig) {
    projectPath = projectConfig.path;
  } else {
    const resolved = resolveProjectFromIssueSync(issueId);
    if (resolved) {
      projectPath = resolved.projectPath;
    }
  }

  if (!projectPath) {
    console.error(chalk.red(`Could not resolve project for ${issueId}`));
    process.exit(1);
  }

  // Determine tracker type
  const isGitHub = issueUpper.startsWith('PAN-');
  let owner: string | undefined;
  let repo: string | undefined;
  let number: number | undefined;

  if (isGitHub) {
    const ghConfig = getGitHubConfig();
    if (ghConfig) {
      owner = ghConfig.owner;
      repo = ghConfig.repo;
      number = parseInt(issueId.replace(/^PAN-/i, ''), 10);
    } else {
      // Fallback for PAN- issues
      owner = 'eltmon';
      repo = 'overdeck';
      number = parseInt(issueId.replace(/^PAN-/i, ''), 10);
    }
  }

  let canonicalState: CanonicalState | null = null;
  let canonicalStateError: string | null = null;
  if (isGitHub && owner && repo && number) {
    try {
      canonicalState = await readGitHubCanonicalState(owner, repo, number);
    } catch (error) {
      canonicalStateError = error instanceof Error ? error.message : String(error);
    }
  }

  // Confirmation
  if (!options.force) {
    console.log(chalk.yellow(`\nClose-out ceremony for ${issueUpper}\n`));
    console.log(chalk.gray(`Issue should normally be in 'verifying-on-main' before close-out.`));
    if (canonicalState && canonicalState !== 'verifying_on_main') {
      console.log(chalk.yellow(`Warning: current canonical state is '${canonicalState}', not 'verifying_on_main'.`));
    } else if (canonicalStateError) {
      console.log(chalk.yellow(`Warning: could not read current canonical state: ${canonicalStateError}`));
    }
    console.log();
    console.log(chalk.gray('This will:'));
    console.log(chalk.gray('  1. Run the Definition-of-Done gate before any cleanup'));
    console.log(chalk.gray('  2. Verify PRD is preserved and archive workspace artifacts'));
    console.log(chalk.gray('  3. Clean up workspace and agent state'));
    console.log(chalk.gray('  4. Close the tracker issue and clear review status'));
    console.log();

    const readline = await import('readline');
    const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
    const confirmed = await new Promise<boolean>((resolve) => {
      rl.question(chalk.yellow(`Proceed with close-out? [y/N] `), (answer) => {
        rl.close();
        resolve(answer.toLowerCase() === 'y' || answer.toLowerCase() === 'yes');
      });
    });

    if (!confirmed) {
      console.log(chalk.gray('Aborted.'));
      return;
    }
  }

  console.log(chalk.blue(`\nRunning close-out for ${issueUpper}...\n`));

  const ctx = {
    issueId,
    projectPath,
    ...(isGitHub && owner && repo && number
      ? { github: { owner, repo, number } }
      : {}),
  };

  const dodAcceptedRows = DOD_ROWS
    .filter(row => row.overridable && options[optionNameForRow(row.id)])
    .map(row => row.id);
  const result = await Effect.runPromise(closeOut(ctx, {
    dodAcceptedRows,
    dodAcceptedBy: process.env.OVERDECK_AGENT_ID || userInfo().username,
  }));

  if (options.json) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  renderDodGate(result);

  // Display step results
  for (const step of result.steps) {
    const icon = step.success ? (step.skipped ? chalk.yellow('⊘') : chalk.green('✓'))
      : chalk.red('✗');
    const msg = step.error
      ? chalk.gray(` — ${step.error}`)
      : step.details?.length
        ? chalk.gray(` — ${step.details.join('; ')}`)
        : '';
    console.log(`  ${icon} ${step.step}${msg}`);
  }

  console.log();

  if (result.success) {
    console.log(chalk.green(`Close-out complete for ${issueUpper}.`));
    // Exit explicitly: close-out opens the SQLite handle (and other resources)
    // whose open handles otherwise keep the event loop alive, so the command
    // hangs after printing "complete". The failure path below already exits;
    // this makes success symmetric. (PAN-1621)
    process.exit(0);
  } else {
    const failedStep = result.steps.find(s => !s.success && !s.skipped);
    console.log(chalk.red(`Close-out failed: ${failedStep?.error || 'Unknown error'}`));
    process.exit(1);
  }
}
